<template>
  <b-container fluid>
    <b-row>
      <!-- En bloc : les boutons passent au-dessus du numéro de retour au lieu de le serrer. -->
      <b-col class="d-block">
        <div v-if="values.id" class="text-right">
          <b-button variant="outline-primary ml-10" size="lg" @click="openFormModal(values.id)">
            Modifier la fiche
            <i class="far fa-pencil ml-10"></i>
          </b-button>
          <b-button variant="outline-primary ml-10" size="lg" @click="$root.exportPdf(exportPdfData)">
            Exporter en PDF
            <i class="far fa-file-alt fa-lg ml-10"></i>
          </b-button>
        </div>
        <div class="d-flex">
          <backlink class="mt-20" />
          <div class="flex-grow-1">
            <h1 class="detail-title">
              Demande de retour
              <span v-if="!isBusy" class="subtitle">#{{ values.return_number }}</span>
              <b-badge variant="primary">{{ values.status }}</b-badge>
            </h1>
          </div>
        </div>
      </b-col>
    </b-row>
    <!-- Avant le chargement : placé entre le <p v-if> et le <b-row v-else>, il capterait le v-else. -->
    <ExchangeBanner v-if="!isBusy" context="return" :id="$route.params.id" />
    <div v-if="isBusy" class="loader"></div>
    <p v-if="isBusy" class="text-center">Les données sont en cours de chargement...</p>
    <b-row v-else>
      <b-col>
        <div class="ibox">
          <div class="ibox-content divided">
            <!-- Récapitulatif en blocs (.detail-recap, cf. _global.scss), puis mode de retour et
                 contacts sur une seconde ligne. -->
            <div class="detail-recap">
              <div class="detail-recap__block">
                <h3><i class="far fa-calendar-alt mr-10"></i>Dates</h3>
                <dl class="detail-list">
                  <dt>{{ labels.axiloc_creation_date }}</dt>
                  <dd>{{ values.axiloc_creation_date || "-" }}</dd>
                  <dt>{{ labels.withdraw_wished_date }}</dt>
                  <dd>{{ values.withdraw_wished_date || "-" }}</dd>
                </dl>
              </div>
              <div class="detail-recap__block">
                <h3><i class="far fa-building mr-10"></i>Affaire</h3>
                <dl class="detail-list">
                  <dt>{{ labels.agency }}</dt>
                  <dd>
                    <!-- Lien vers la fiche seulement si l'API a renvoyé l'id : un retour
                         rattaché à une affaire sans agence n'a rien à ouvrir. -->
                    <router-link
                      v-if="rawValues.agency && rawValues.agency.id && values.agency"
                      :to="{ name: 'AgencyDetail', params: { id: rawValues.agency.id } }"
                      target="_blank"
                      >{{ values.agency }}</router-link
                    >
                    <template v-else>{{ values.agency || "-" }}</template>
                  </dd>
                  <dt>{{ labels.business }}</dt>
                  <dd>
                    <router-link
                      v-if="rawValues.business && rawValues.business.id && values.business"
                      :to="{ name: 'BusinessDetail', params: { id: rawValues.business.id } }"
                      target="_blank"
                      >{{ values.business }}</router-link
                    >
                    <template v-else>{{ values.business || "-" }}</template>
                  </dd>
                </dl>
              </div>
              <div class="detail-recap__block">
                <h3><i class="far fa-user mr-10"></i>Demandeur</h3>
                <dl class="detail-list">
                  <template v-if="values.user">
                    <dt>{{ labels.user }}</dt>
                    <dd>{{ values.user }}</dd>
                  </template>
                  <dt>{{ labels.asker_email }}</dt>
                  <dd>
                    <a v-if="values.asker_email" :href="'mailto:' + values.asker_email">{{ values.asker_email }}</a>
                    <template v-else>-</template>
                  </dd>
                  <dt>{{ labels.asker_number }}</dt>
                  <dd>{{ values.asker_number || "-" }}</dd>
                </dl>
              </div>
              <div class="detail-recap__block">
                <h3><i class="far fa-box mr-10"></i>Matériels</h3>
                <!-- Libellés courts : ceux du formulaire sont les questions posées au demandeur. -->
                <dl class="detail-list">
                  <dt>Conditionnement</dt>
                  <dd class="detail-recap__comment">{{ values.comment || "-" }}</dd>
                  <dt>Matériels en panne</dt>
                  <dd class="detail-recap__comment">{{ values.failure_warning_comment || "-" }}</dd>
                </dl>
              </div>
            </div>
            <b-row>
              <b-col>
                <template v-if="values.material_retrieving_place == 'axiloc'">
                  <ul>
                    <li>
                      <span v-if="values.material_retrieving_place == 'axiloc'" class="badge badge-axiloc">Dépôt direct à l'entrepôt
                        ESM</span>
                    </li>
                  </ul>
                  <dl v-if="values.return_warehouse">
                    <dt>Entrepôt de dépôt :</dt>
                    <dd>
                      <router-link
                        v-if="values.return_warehouse.id"
                        :to="{ name: 'WarehouseDetail', params: { id: values.return_warehouse.id } }"
                        target="_blank"
                        >{{ warehouseLabel(values.return_warehouse) }}</router-link
                      >
                      <template v-else>{{ warehouseLabel(values.return_warehouse) }}</template>
                      <span v-if="values.return_warehouse.address"><br />{{ values.return_warehouse.address }}</span>
                      <span v-if="values.return_warehouse.phone"><br />Tél : {{ values.return_warehouse.phone }}</span>
                    </dd>
                  </dl>
                </template>
                <template v-if="values.material_retrieving_place == 'chronopost'">
                  <ul>
                    <li>
                      <span class="badge badge-chronopost">Retour Chronopost</span>
                    </li>
                  </ul>
                  <dl v-if="values.return_warehouse">
                    <dt>Entrepôt de destination :</dt>
                    <dd>
                      <router-link
                        v-if="values.return_warehouse.id"
                        :to="{ name: 'WarehouseDetail', params: { id: values.return_warehouse.id } }"
                        target="_blank"
                        >{{ warehouseLabel(values.return_warehouse) }}</router-link
                      >
                      <template v-else>{{ warehouseLabel(values.return_warehouse) }}</template>
                    </dd>
                  </dl>
                </template>
                <template v-if="values.material_retrieving_place != 'axiloc' && values.material_retrieving_place != 'chronopost'">
                  <div class="return-type-container">
                    <h3>Retrait</h3>
                    <span v-if="values.material_retrieving_place == 'agency'"
                      class="badge badge-agency">Retour depuis une agence</span>
                    <span v-if="values.material_retrieving_place == 'project'"
                      class="badge badge-project">Retour depuis un chantier</span>
                    <span v-if="values.material_retrieving_place == 'other'"
                      class="badge badge-other">Autre retour</span>
                  </div>
                  <ul>
                    <li>{{ values.withdraw_name }} : {{ values.withdraw_address }}</li>
                  </ul>
                  <dl>
                    <dt>{{ labels.required_truck_tailgate }}</dt>
                    <dd>{{ values.required_truck_tailgate == 1 ? "Oui" : "Non" }}</dd>
                    <dt>{{ labels.reachable_by_semitrailer_truck }}</dt>
                    <dd>{{ values.reachable_by_semitrailer_truck == 1 ? "Oui" : "Non" }}</dd>
                    <dt>{{ labels.handling_units_number }} :</dt>
                    <dd>{{ values.handling_units_number || "-" }}</dd>
                    <dt>{{ labels.tracking_number }} :</dt>
                    <dd>{{ values.tracking_number || "-" }}</dd>
                    <dt>Entrepôt de destination :</dt>
                    <dd>
                      <template v-if="values.return_warehouse">
                        <router-link
                          v-if="values.return_warehouse.id"
                          :to="{ name: 'WarehouseDetail', params: { id: values.return_warehouse.id } }"
                          target="_blank"
                          >{{ warehouseLabel(values.return_warehouse) }}</router-link
                        >
                        <template v-else>{{ warehouseLabel(values.return_warehouse) }}</template>
                      </template>
                      <template v-else>-</template>
                    </dd>
                  </dl>
                </template>
                <dl>
                  <dt>{{ labels.total_weight }} :</dt>
                  <dd>{{ values.total_weight || "-" }}</dd>
                </dl>
              </b-col>
              <b-col v-if="values.material_retrieving_place != 'axiloc' && values.material_retrieving_place != 'chronopost'">
                <h3>Premier contact</h3>
                <ul>
                  <li>
                    <i class="far fa-user"></i>
                    {{ values.first_contact_name || "-" }}
                  </li>
                  <li>
                    <i class="far fa-phone"></i>
                    <a v-if="values.first_contact_phone" :href="'tel:' + values.first_contact_phone">{{
                      values.first_contact_phone
                    }}</a>
                    <template v-else>-</template>
                  </li>
                </ul>
                <h3>Second contact</h3>
                <ul v-if="values.second_contact_name || values.second_contact_phone">
                  <li>
                    <i class="far fa-user"></i>
                    {{ values.second_contact_name || "-" }}
                  </li>
                  <li>
                    <i class="far fa-phone"></i>
                    <a v-if="values.second_contact_phone" :href="'tel:' + values.second_contact_phone">{{
                      values.second_contact_phone
                    }}</a>
                    <template v-else>-</template>
                  </li>
                </ul>
                <p v-else class="text-muted">Aucune information renseignée pour le second contact.</p>
              </b-col>
            </b-row>
            <div v-if="can_process_on_mobile && values.id" class="px-4 py-3">
              <router-link :to="{
              name: 'BusinessReturnMobileDetail',
              params: { id: values.id },
            }" target="_blank">Réceptionner sur mobile</router-link>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row v-if="values.stored_files && values.stored_files.length">
      <b-col>
        <div class="ibox">
          <div class="ibox-title"><i class="far fa-folder"></i>Fichiers joints</div>
          <div class="ibox-content">
            <div class="table-wrapper">
              <b-table hover :fields="[{ key: 'file', label: 'Fichier' }]" :items="values.stored_files">
                <template slot="file" slot-scope="data">
                  <a :href="BACK_FILES_URL + data.value" target="_blank">{{ data.value }}</a>
                </template>
              </b-table>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row v-if="values.unique_references && values.unique_references.length">
      <b-col>
        <div class="ibox">
          <div class="ibox-title">Références uniques</div>
          <div class="ibox-content">
            <div class="table-wrapper">
              <b-table hover :fields="business_return_reference_fields" :items="values.unique_references">
                <template slot="product" slot-scope="data">
                  <router-link v-if="data.item.product" :to="{
                    name: 'ProductDetail',
                    params: { id: data.item.product.id },
                  }">{{ data.item.product.name }}</router-link>
                </template>
                <template slot="internal_code" slot-scope="data">
                  <router-link :to="{ name: 'ReferenceDetail', params: { id: data.item.id } }">{{
                    data.item.internal_code
                  }}</router-link>
                </template>
                <template slot="status" slot-scope="data">
                  <b-badge variant="primary">{{ data.value.name }}</b-badge>
                </template>
              </b-table>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <AdminModalForm v-if="config.useNewModal || config.useEditModal" :config="config" :id_to_edit="id_to_edit" />
  </b-container>
</template>

<script>
import config, { warehouseLabel } from "./config";
import ExchangeBanner from "@/components/Exchange/ExchangeBanner";
import reference_config from "@/components/Reference/config";
import AdminModalForm from "@/components/Global/AdminModalForm";

export default {
  name: "BusinessReturnDetail",
  components: {
    ExchangeBanner,
    AdminModalForm,
  },
  data() {
    return {
      config: config,
      reference_config: reference_config,
      fields: {},
      labels: {},
      isBusy: false,
      business_return_reference_fields: [],
      id_to_edit: null,
      lists: [],
      lists_fields: {},
    };
  },
  computed: {
    rawValues: function () {
      return this.$store.getters[config.storeNamespace + "/getItem"](this.$route.params.id) || {};
    },
    values: function () {
      return this.$root.formatData(
        this.rawValues,
        this.config.fields.filter((field) => field.inDetail == true)
      );
    },
    // Raccourci vers l'écran de retour mobile, même règle que « Préparer sur mobile » d'une
    // commande : réservé aux super-admins en production, ouvert à tous ailleurs pour tester.
    can_process_on_mobile: function () {
      return (
        process.env.NODE_ENV !== "production" ||
        (this.$store.getters["auth/roles"] || []).includes("ROLE_SUPER_ADMIN")
      );
    },
    BACK_FILES_URL: function () {
      return process.env.BACK_FILES_URL;
    },
    listsItems: function () {
      let listsItems = {};
      if (this.lists && this.lists.length) {
        this.lists.forEach((list) => {
          if (this.values[list.name]) {
            listsItems[list.name] = this.$root.formatDatas(
              this.values[list.name],
              list.config.fields
            );
          }
        });
      }
      return listsItems;
    },
    exportPdfData: function () {
      let exportPdfData = {
        title: this.config.pdf_export_title
          ? this.config.pdf_export_title(this.rawValues, this.values, this)
          : this.values[this.config.titleField],
        link: this.config.pdf_export_link
          ? this.config.pdf_export_link(this.rawValues, this.values, this)
          : null,
        infos: [],
      };

      if (this.config.pdf_export_logo) {
        exportPdfData.logo = this.config.pdf_export_logo;
      }

      if (this.config.pdf_export_extra_content_before) {
        exportPdfData.extra_content_before = this.config.pdf_export_extra_content_before(
          this.rawValues,
          this.values,
          this
        );
      }

      if (this.config.pdf_export_custom_main_content) {
        exportPdfData.custom_main_content = this.config.pdf_export_custom_main_content(
          this.rawValues,
          this.values,
          this
        );
      } else {
        this.config.fields
          .filter((field) => field.inPdfExport == true)
          .forEach((field) => {
            exportPdfData.infos.push([
              field.label + " :",
              field.name == 'material_retrieving_place'
              ? (this.values['material_retrieving_place'] == 'axiloc'
                ? {text: "Dépôt direct à l'entrepôt ESM", background: '#519e5f'}
                : {text: this.$store.getters['business_return/getMaterialRetrievingPlaceBySlug'](this.values[field.name]), background: this.$store.getters['business_return/getMaterialRetrievingPlaceColorBySlug'](this.values[field.name])})
              : (field.type == "yesno"
                ? this.values[field.name] == true
                  ? "Oui"
                  : "Non"
                : this.values[field.name] || null),
            ]);
          });
      }

      exportPdfData.lists = [];
      if (this.lists && this.lists.length) {
        this.lists.forEach((list) => {
          let currentList = {
            label: list.label,
            infos: [this.lists_fields[list.name].map((f) => f.label)],
          };
          this.listsItems[list.name].forEach((item) => {
            let infosItem = [];
            list.fields.forEach((field) => {
              infosItem.push(item[field] || null);
            });
            currentList.infos.push(infosItem);
          });
          exportPdfData.lists.push(currentList);
        });
      }

      if (this.config.pdf_export_extra_lists) {
        exportPdfData.lists.push(
          ...this.config.pdf_export_extra_lists(this.rawValues, this.values, this)
        );
      }

      if (this.config.pdf_export_extra_content_after) {
        exportPdfData.extra_content_after = this.config.pdf_export_extra_content_after(
          this.rawValues,
          this.values,
          this
        );
      }

      return exportPdfData;
    },
  },
  mounted() {
    // Chargement des champs à afficher dans le détail depuis la config
    // Et récupération des libellés
    this.fields = config.fields.filter((field) => field.inDetail == true);
    config.fields
      .filter((field) => field.inDetail == true)
      .forEach((field) => {
        this.labels[field.name] = field.label;
      });

    this.isBusy = true;
    this.$store
      .dispatch(config.storeNamespace + "/getItem", {
        id: this.$route.params.id,
      })
      .then(() => {
        // Test si le contenu demandé est bien trouvé
        let self = this;
        if (Object.entries(this.rawValues).length <= 0) {
          this.$toastr.error(
            "La fiche demandée n'a pas été trouvée. Assure-vous que l'URL saisie soit correcte."
          );
          setTimeout(function () {
            self.$router.go(-1);
          }, 5000);
          return;
        }

        // Gestion des listes additionnelles
        this.lists = this.config.fields.filter((field) => field.type == "list");
        if (this.lists && this.lists.length) {
          let lists_fields = {};
          this.lists.forEach((list) => {
            lists_fields[list.name] = [];

            list.fields.forEach((field) => {
              lists_fields[list.name].push({
                key: field,
                label: list.config.fields.find((f) => f.name == field).label,
              });
            });
          });
          this.lists_fields = lists_fields;
        }

        this.isBusy = false;
      });

    // Préparation des champs pour la liste des références
    this.business_return_reference_fields = reference_config.fields
      .filter((field) => ["product", "internal_code", "status"].includes(field.name))
      .map((field) => ({
        key: field.name,
        label: field.label,
      }));
  },
  methods: {
    // Entrepôt de destination : code interne (LG…) suivi du nom
    warehouseLabel,
    openFormModal(id) {
      this.id_to_edit = id;
      this.$root.$emit("bv::show::modal", "admin_modal_form");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/_variables.scss";

.badge {
  &.badge-axiloc {
    background-color: $primary;
    color: #ffffff;
  }

  &.badge-chronopost {
    background-color: $yellow;
    color: #ffffff;
  }

  &.badge-agency {
    background-color: $blue;
    color: #ffffff;
  }

  &.badge-project {
    background-color: $orange;
    color: #ffffff;
  }

  &.badge-other {
    background-color: $purple;
    color: #ffffff;
  }
}

.return-type-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}
</style>



// WEBPACK FOOTER //
// src/components/BusinessReturn/BusinessReturnDetail.vue